import React, { useEffect, useMemo, useState } from "react";
import Seo from "../components/site/Seo";
import PageHero from "../components/site/PageHero";
import Reveal from "../components/site/Reveal";
import { PlaceholderNote, ArrowLink } from "../components/site/Bits";
import { WorkCard } from "../components/home/FeaturedWork";
import ClosingCTA from "../components/home/ClosingCTA";
import { SEO, WORK_FILTERS, WORK_PAGE } from "../lib/content";
import { IMAGES } from "../lib/images";
import { fetchProjects } from "../lib/api";

const RATIOS = ["16 / 11", "4 / 5", "16 / 10", "1 / 1", "4 / 5", "16 / 10"];

export default function Work() {
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState("ALL");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchProjects()
      .then((data) => alive && setProjects(data))
      .catch(() => alive && setProjects([]))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  const visible = useMemo(
    () => (filter === "ALL" ? projects : projects.filter((p) => p.category === filter)),
    [projects, filter],
  );

  return (
    <>
      <Seo title={SEO.work.title} description={SEO.work.description} path="/work" />

      <PageHero
        eyebrow="Selected Work"
        titleLines={WORK_PAGE.heading}
        intro="Work across events, advertising, digital content, production and social impact — built by one team, end to end."
        image={IMAGES.work.hero}
        compact
      />

      <section className="lk-section" data-testid="work-grid-section">
        <div className="lk-container-wide">
          {/* filter bar */}
          <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
            <div
              className="no-scrollbar -mx-1 flex gap-2 overflow-x-auto px-1 py-1"
              role="tablist"
              aria-label="Filter projects by category"
            >
              {WORK_FILTERS.map((option) => {
                const active = filter === option;
                return (
                  <button
                    key={option}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    data-testid={`work-filter-${option.toLowerCase().replace(/\s+/g, "-")}`}
                    data-work-filter={option}
                    onClick={() => setFilter(option)}
                    className="shrink-0 rounded-full border px-5 py-[10px] text-[11.5px] font-semibold uppercase transition-all duration-300"
                    style={{
                      letterSpacing: "0.14em",
                      background: active ? "#352A59" : "transparent",
                      color: active ? "#fff" : "rgba(23,21,34,0.7)",
                      borderColor: active ? "#352A59" : "rgba(23,21,34,0.16)",
                    }}
                  >
                    {option}
                  </button>
                );
              })}
            </div>
            <p className="text-[12.5px]" style={{ color: "rgba(23,21,34,0.48)" }} data-testid="work-count">
              {loading ? "Loading projects…" : `${visible.length} ${visible.length === 1 ? "project" : "projects"}`}
            </p>
          </div>

          {loading && (
            <div className="mt-10 grid gap-x-12 gap-y-10 sm:grid-cols-2 lg:grid-cols-3">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <div
                  key={i}
                  className="w-full animate-pulse"
                  style={{ aspectRatio: "16 / 11", background: "var(--lykit-lavender)" }}
                />
              ))}
            </div>
          )}

          {!loading && visible.length === 0 && (
            <div className="mt-11 max-w-[52ch]" data-testid="work-empty-state">
              <h2 className="display-md">NOTHING HERE YET.</h2>
              <p className="mt-5 lead">
                No projects in this category so far. Tell us what you are building and it could be the next one.
              </p>
              <div className="mt-8">
                <ArrowLink to="/contact">Start a conversation</ArrowLink>
              </div>
            </div>
          )}

          {!loading && visible.length > 0 && (
            <>
              <div className="mt-10 grid gap-x-12 gap-y-11 sm:grid-cols-2 lg:grid-cols-3">
                {visible.map((project, i) => (
                  <div
                    key={project.id || project.slug}
                    className={i % 3 === 1 ? "lg:pt-[clamp(0px,3vw,56px)]" : ""}
                  >
                    <WorkCard project={project} ratio={RATIOS[i % RATIOS.length]} index={i % 3} />
                  </div>
                ))}
              </div>

              <Reveal className="mt-11">
                <PlaceholderNote>{WORK_PAGE.note}</PlaceholderNote>
              </Reveal>
            </>
          )}
        </div>
      </section>

      <ClosingCTA />
    </>
  );
}
